import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const A4_W_MM = 210;
const A4_H_MM = 297;

// Badge formats: std = credit-card (CR80 85.6×53.98), xl = 10×7 cm.
// A4 grid per format: std 2×5 (gap 6mm), xl 2×3 (gap 4mm, ~3mm side margins).
const BADGE_FORMATS = {
    std: { w: 85.6, h: 53.98, cols: 2, rows: 5, gap: 6 },
    xl: { w: 100, h: 70, cols: 2, rows: 3, gap: 4 },
};
const getFormat = (key) => BADGE_FORMATS[key] || BADGE_FORMATS.std;

// PRINT QUALITY CONFIGURATION
// ---------------------------
// scale 4.0 = ~340dpi @ tesserino (85.6×54mm @ 4dpi/mm × 4 = 1370×864 px capture)
//   → professional print quality (300+ dpi is the industry standard for offset/laser)
// scale 2.5 was ~210dpi which is fine for screens but visibly soft on paper.
// Trade-off: ~2.5x more processing time and ~2x file size vs. old default.
const CAPTURE_SCALE = 4.0;
// 0.98 ≈ visually indistinguishable from PNG, ~3x smaller than 1.0.
// At scale 4.0 + quality 0.98 a single badge is ~300KB; an A4 with 10 badges ≈3MB.
const JPEG_QUALITY = 0.98;

// html2canvas 1.4.1 ignores object-fit and stretches <img> into its box: pre-crop the badge
// photo (cover, center-top — same as the CSS) so legacy photos with a different ratio
// are not distorted in the PDF.
const fitBadgePhotos = (sourceEl, clonedDoc, targetAttr) => {
    const clones = clonedDoc.querySelectorAll(`[${targetAttr}] img.badge-photo`);
    sourceEl.querySelectorAll("img.badge-photo").forEach((src, i) => {
        const clone = clones[i];
        if (!clone || !src.complete || !src.naturalWidth || !src.clientWidth) return;
        const boxRatio = src.clientWidth / src.clientHeight;
        const imgRatio = src.naturalWidth / src.naturalHeight;
        if (Math.abs(boxRatio - imgRatio) < 0.005) return;
        let sw = src.naturalWidth;
        let sh = src.naturalHeight;
        let sx = 0;
        if (imgRatio > boxRatio) {
            sw = Math.round(sh * boxRatio);
            sx = Math.round((src.naturalWidth - sw) / 2);
        } else {
            sh = Math.round(sw / boxRatio);
        }
        const c = document.createElement("canvas");
        c.width = sw;
        c.height = sh;
        c.getContext("2d").drawImage(src, sx, 0, sw, sh, 0, 0, sw, sh);
        clone.src = c.toDataURL("image/jpeg", 0.95);
    });
};

const captureElement = async (el) => {
    // html2canvas clones the entire document to compute styles; with 100+ table rows this is slow.
    // Use `onclone` to mark the target element so we can remove all other DOM siblings in the clone.
    const TARGET_ATTR = "data-h2c-target";
    el.setAttribute(TARGET_ATTR, "1");
    try {
        const canvas = await html2canvas(el, {
            scale: CAPTURE_SCALE,
            useCORS: true,
            allowTaint: false,
            backgroundColor: "#ffffff",
            logging: false,
            imageTimeout: 15000,
            // letterRendering improves anti-aliasing for crisp text on print.
            // Available in html2canvas >= 1.x; ignored if unsupported.
            letterRendering: true,
            // Match the source viewport so the layout is captured 1:1 with no reflow
            windowWidth: el.offsetWidth,
            windowHeight: el.offsetHeight,
            // Disable foreignObject rendering which can blur text on retina
            foreignObjectRendering: false,
            onclone: (clonedDoc) => {
                // Find the target in the clone and reduce DOM noise: remove all body children that don't contain it.
                const target = clonedDoc.querySelector(`[${TARGET_ATTR}]`);
                if (!target) return;
                fitBadgePhotos(el, clonedDoc, TARGET_ATTR);
                const body = clonedDoc.body;
                // Walk up from target to body, marking each ancestor as "keep"
                const keep = new Set();
                let p = target;
                while (p && p !== body) {
                    keep.add(p);
                    p = p.parentElement;
                }
                // Remove top-level body children that don't contain the target
                Array.from(body.children).forEach((child) => {
                    if (!keep.has(child) && !child.contains(target)) {
                        child.remove();
                    }
                });
                // Also remove sibling chains that don't lead to the target
                const pruneSiblings = (node) => {
                    if (!node || node === body) return;
                    const parent = node.parentElement;
                    if (!parent) return;
                    Array.from(parent.children).forEach((sib) => {
                        if (sib !== node && !sib.contains(target)) {
                            sib.remove();
                        }
                    });
                    pruneSiblings(parent);
                };
                pruneSiblings(target);
            },
        });
        // High-quality JPEG (0.98 ≈ visually lossless) — 3-5x smaller than PNG
        return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    } finally {
        el.removeAttribute(TARGET_ATTR);
    }
};

const transformDataUrl = (dataUrl, { rotate180 = false, facsimile = false } = {}) =>
    new Promise((resolve, reject) => {
        if (!rotate180 && !facsimile) {
            resolve(dataUrl);
            return;
        }
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
            const c = document.createElement("canvas");
            c.width = img.width;
            c.height = img.height;
            const ctx = c.getContext("2d");
            // Preserve resampling quality when applying transforms
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            if (rotate180) {
                ctx.translate(c.width, c.height);
                ctx.rotate(Math.PI);
            }
            ctx.drawImage(img, 0, 0);
            ctx.resetTransform && ctx.resetTransform();
            if (facsimile) {
                ctx.save();
                ctx.translate(c.width / 2, c.height / 2);
                ctx.rotate(-Math.PI / 8);
                ctx.fillStyle = "rgba(220, 38, 38, 0.32)";
                ctx.strokeStyle = "rgba(220, 38, 38, 0.5)";
                ctx.lineWidth = 4;
                const fontSize = Math.floor(c.height * 0.2);
                ctx.font = `bold ${fontSize}px "Work Sans", sans-serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.strokeText("FAC-SIMILE", 0, 0);
                ctx.fillText("FAC-SIMILE", 0, 0);
                ctx.restore();
            }
            resolve(c.toDataURL("image/jpeg", JPEG_QUALITY));
        };
        img.src = dataUrl;
    });

const buildSingleBadgePdf = async (frontEl, backEl, opts = {}) => {
    const { facsimile = false, rotateBack = false, format = "std" } = opts;
    const F = getFormat(format);

    const frontRaw = await captureElement(frontEl);
    const frontImg = await transformDataUrl(frontRaw, { facsimile });

    // A4 duplex-aligned layout:
    // page 1 → front at TOP-LEFT; page 2 → back at TOP-RIGHT (horizontally mirrored).
    // With a long-edge duplex print (default for portrait) the back lands exactly
    // behind the front, so the cut-out card is perfectly aligned fronte/retro.
    const MARGIN_MM = 12;
    const frontX = MARGIN_MM;
    const cardY = MARGIN_MM;
    const backX = A4_W_MM - MARGIN_MM - F.w;

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
    pdf.addImage(frontImg, "JPEG", frontX, cardY, F.w, F.h, undefined, "FAST");
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.1);
    pdf.rect(frontX, cardY, F.w, F.h);

    if (backEl) {
        const backRaw = await captureElement(backEl);
        const backImg = await transformDataUrl(backRaw, { rotate180: rotateBack, facsimile });
        pdf.addPage("a4", "portrait");
        pdf.addImage(backImg, "JPEG", backX, cardY, F.w, F.h, undefined, "FAST");
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.1);
        pdf.rect(backX, cardY, F.w, F.h);
    }
    return pdf;
};

export const downloadSingleBadgePDF = async (frontEl, backEl, filename, opts = {}) => {
    const pdf = await buildSingleBadgePdf(frontEl, backEl, opts);
    pdf.save(filename || "tesserino.pdf");
};

export const printSingleBadge = async (frontEl, backEl, opts = {}) => {
    const pdf = await buildSingleBadgePdf(frontEl, backEl, opts);
    pdf.autoPrint();
    const blobUrl = pdf.output("bloburl");
    const w = window.open(blobUrl, "_blank");
    if (!w) pdf.save("tesserino-stampa.pdf");
};

/**
 * A4 export: page 1 = fronts, page 2 = backs.
 * Backs are placed at the HORIZONTALLY MIRRORED grid position (col 0 ↔ col 1)
 * so that a long-edge duplex print puts each back exactly behind its front
 * for a precise fronte/retro cut. If `includeBack` is false, only fronts are emitted.
 * `items` is an array of { front: HTMLElement, back?: HTMLElement }
 */
// Compose a back image by overlaying a fresh QR on top of the cached back template.
// templateDataUrl: full back capture (front+back unmodified), qrCanvas: captured QR pixel data, qrRect: where to draw it.
const composeBackWithQr = async (templateDataUrl, qrCanvas, qrRect) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
            const c = document.createElement("canvas");
            c.width = img.width;
            c.height = img.height;
            const ctx = c.getContext("2d");
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0);
            // White-out the QR area to remove the original QR, then draw the new one
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(qrRect.x, qrRect.y, qrRect.w, qrRect.h);
            ctx.drawImage(qrCanvas, qrRect.x, qrRect.y, qrRect.w, qrRect.h);
            resolve(c.toDataURL("image/jpeg", JPEG_QUALITY));
        };
        img.src = templateDataUrl;
    });

const captureQrOnly = async (qrEl) => {
    const canvas = await html2canvas(qrEl, {
        scale: CAPTURE_SCALE,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 8000,
        useCORS: true,
        letterRendering: true,
        foreignObjectRendering: false,
    });
    return canvas;
};

const computeQrRect = (backEl, qrEl) => {
    const backRect = backEl.getBoundingClientRect();
    const qRect = qrEl.getBoundingClientRect();
    return {
        x: Math.round((qRect.left - backRect.left) * CAPTURE_SCALE),
        y: Math.round((qRect.top - backRect.top) * CAPTURE_SCALE),
        w: Math.round(qRect.width * CAPTURE_SCALE),
        h: Math.round(qRect.height * CAPTURE_SCALE),
    };
};

const buildA4BadgesPdf = async (items, opts = {}) => {
    const { includeBack = true, facsimile = false, rotateBack = false, onProgress, format = "std" } = opts;
    const F = getFormat(format);

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });

    const cols = F.cols;
    const rows = F.rows;
    const perPage = cols * rows;

    const totalGridW = cols * F.w + (cols - 1) * F.gap;
    const totalGridH = rows * F.h + (rows - 1) * F.gap;
    const startX = (A4_W_MM - totalGridW) / 2;
    const startY = (A4_H_MM - totalGridH) / 2;

    const placeOnPage = async (imgs, pageIdx, { mirror = false } = {}) => {
        const startIdx = pageIdx * perPage;
        const endIdx = Math.min(startIdx + perPage, imgs.length);
        for (let i = startIdx; i < endIdx; i++) {
            const posIndex = i - startIdx;
            const col = posIndex % cols;
            const row = Math.floor(posIndex / cols);
            const baseX = startX + col * (F.w + F.gap);
            // Mirror horizontally for the backs page: long-edge duplex flips the
            // sheet around the vertical axis, so the back must sit on the opposite side.
            const x = mirror ? A4_W_MM - baseX - F.w : baseX;
            const y = startY + row * (F.h + F.gap);
            pdf.addImage(imgs[i], "JPEG", x, y, F.w, F.h, undefined, "FAST");
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.1);
            pdf.rect(x, y, F.w, F.h);
        }
    };

    // Pre-capture and transform every image so html2canvas runs in series (avoids race)
    const frontImgs = [];
    const backImgs = [];
    // Cache the back "template" — captured once from the first back element.
    // Subsequent backs only need to swap the QR (cheap: ~50ms each instead of ~1s).
    let backTemplateRaw = null;
    let backTemplateQrRect = null;

    const total = items.length;
    for (let idx = 0; idx < items.length; idx++) {
        const it = items[idx];
        if (onProgress) onProgress({ current: idx + 1, total });
        if (it.front) {
            const raw = await captureElement(it.front);
            frontImgs.push(await transformDataUrl(raw, { facsimile }));
        }
        if (includeBack && it.back) {
            const qrEl = it.back.querySelector("[data-qr-region]");

            // Strategy: capture the very first back fully; for the rest, swap only the QR.
            // Fallback to full capture if the QR region selector ever fails or facsimile is on (watermark over QR).
            const canReuseTemplate = !!qrEl && !facsimile && backTemplateRaw !== null;

            if (canReuseTemplate) {
                const qrCanvas = await captureQrOnly(qrEl);
                const composedRaw = await composeBackWithQr(backTemplateRaw, qrCanvas, backTemplateQrRect);
                backImgs.push(await transformDataUrl(composedRaw, { rotate180: rotateBack, facsimile: false }));
            } else {
                const raw = await captureElement(it.back);
                if (backTemplateRaw === null && qrEl && !facsimile) {
                    // Cache as template for next iterations
                    backTemplateRaw = raw;
                    backTemplateQrRect = computeQrRect(it.back, qrEl);
                }
                backImgs.push(await transformDataUrl(raw, { rotate180: rotateBack, facsimile }));
            }
        }
    }
    if (onProgress) onProgress({ current: total, total, phase: "rendering" });

    const totalPages = Math.ceil(frontImgs.length / perPage);
    for (let p = 0; p < totalPages; p++) {
        if (p > 0) pdf.addPage("a4", "portrait");
        await placeOnPage(frontImgs, p);

        if (includeBack && backImgs.length === frontImgs.length) {
            pdf.addPage("a4", "portrait");
            await placeOnPage(backImgs, p, { mirror: true });
        }
    }
    return pdf;
};

export const downloadA4BadgesPDF = async (items, filename, opts = {}) => {
    if (!items || items.length === 0) return;
    const pdf = await buildA4BadgesPdf(items, opts);
    pdf.save(filename || "tesserini-a4.pdf");
};

export const printA4Badges = async (items, opts = {}) => {
    if (!items || items.length === 0) return;
    const pdf = await buildA4BadgesPdf(items, opts);
    pdf.autoPrint();
    const blobUrl = pdf.output("bloburl");
    const w = window.open(blobUrl, "_blank");
    if (!w) pdf.save("tesserini-a4-stampa.pdf");
};

export const sanitizeFilename = (s) =>
    (s || "tesserino")
        .toString()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9-_]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();
